/**
 * Reusable Region Filter component for reports.
 * Shows a dropdown of regions with "All Regions" default and "No Region" option.
 * Available to Admin / Senior Scheduler / Scheduler only.
 */
import React, { useState, useEffect } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MapPin } from 'lucide-react';
import api from '../services/api';

// Cache: loaded once per session, shared across all reports
let _cachedRegions = null;
let _cacheOrgId = null;

export default function RegionFilter({ value, onChange, className = '' }) {
  const [regions, setRegions] = useState(_cachedRegions || []);
  const [loading, setLoading] = useState(!_cachedRegions);

  useEffect(() => {
    if (_cachedRegions) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/regions');
        const data = res.data;
        if (!cancelled) {
          const list = data.regions || [];
          _cachedRegions = list;
          setRegions(list);
        }
      } catch {
        // Silently fail — filter won't show regions
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) return null;
  if (regions.length === 0) return null; // No regions configured — hide filter

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <MapPin className="w-4 h-4 text-muted-foreground shrink-0" />
      <Select
        value={value === null || value === undefined ? 'all' : String(value)}
        onValueChange={(v) => onChange(v === 'all' ? null : parseInt(v, 10))}
      >
        <SelectTrigger className="w-[180px] h-9" data-testid="region-filter">
          <SelectValue placeholder="All Regions" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Regions</SelectItem>
          <SelectItem value="0">No Region</SelectItem>
          {regions.map((r) => (
            <SelectItem key={r.id} value={String(r.id)}>
              {r.code ? `${r.code} — ${r.name}` : r.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

/** Invalidate cache when regions are created/edited */
export function invalidateRegionCache() {
  _cachedRegions = null;
}
